import {
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import useDropdowns from 'src/hooks/useDropdowns'
import { AddUnit } from 'src/redux/actions'
import { uploadToS3 } from 'src/util/uploadToS3'
import * as Yup from 'yup'

import { StatusList, VisibilityList } from '../../util/constants'
import { generateUniqueFileName, hrs, ImageRatioSize, minutes } from '../../util/helpers'
import VideoThumbnailField from '../exercise/FormTypes/Fields/VideoThumbnailField'
import VideoUploaderField from '../exercise/FormTypes/Fields/VideoUploaderField'
import DropdownMenu from '../helperComponents/DropdownMenu'
import LoadingButton from '../helperComponents/LoadingButton'

const AddEditCourse = ({
  selectedRow = {},
  toggleDrawer,
  refetch,
  actionTitle,
  setActiveStep,
  setFirstStepData
}) => {

  const { dropdowns } = useDropdowns()

  const courseDifficultyLevel = useSelector(
    store => store.mainReducer.courseDifficultyLevel
  )

  const initialValues = {
    name: (selectedRow && selectedRow?.name) || '',
    description: (selectedRow && selectedRow?.description) || '',
    course: (selectedRow && selectedRow?.course?._id) || '',
    difficultyLevel:
      (selectedRow && selectedRow?.difficultyLevel) || 'interestingStuff',
    sortOrder: (selectedRow && selectedRow?.sortOrder) || '',
    video: selectedRow?.video || '',
    videoThumbnail: (selectedRow && selectedRow?.videoThumbnail) || '',
    courseDetailsThumbnail: (selectedRow && selectedRow?.courseDetailsThumbnail) || '',
    isActive:
      selectedRow && selectedRow.isActive === undefined
        ? true
        : selectedRow && selectedRow?.isActive,
    hrs: selectedRow?.hrs || "00",
    minutes: selectedRow?.minutes || "00",
    unPublished:
      selectedRow && selectedRow.unPublished === undefined
        ? false
        : selectedRow && selectedRow?.unPublished,
  }
  const dispatch = useDispatch()

  const validationSchema = Yup.object().shape({
    name: Yup.string().min(1, 'Minimum 1 letters').max(200, 'Maximum 200 characters').required('Required'),
    sortOrder: Yup.number().min(0),
    description: Yup.string().min(5, 'Minimum 5 letters').required('Required'),
    course: Yup.string().min(5, 'Minimum 5 letters').required('Required'),
  })

  const handleSubmit = async values => {
    // same shape as initial values
    const reqData = selectedRow._id
      ? { ...values, _id: selectedRow._id }
      : { ...values }
    for (let key in reqData) {
      if (reqData[key] instanceof File) {
        let file = reqData[key]
        const uniqueName = generateUniqueFileName(file);
        const uniqueFile = new File([file], uniqueName, { type: file.type });
        await uploadToS3(uniqueFile);
        reqData[key] = uniqueName;
      }
    }
    reqData.hrs = reqData.hrs ? reqData.hrs : "00"
    reqData.minutes = reqData.minutes ? reqData.minutes : "00"
    const response = await dispatch(AddUnit(reqData));
    if (!response) return;

    switch (actionTitle) {
      case "create-course":
        setFirstStepData({
          languageId: response?.course,
          courseId: response?._id,
          difficultyLevel: response?.difficultyLevel,
        });
        setActiveStep(1);
        break;

      default:
        toggleDrawer();
        break;
    }

    refetch();

  }

  const onThumbnailChange = ({ target: { files } }, setFieldValue) => {
    setFieldValue('videoThumbnail', files[0])
  }

  const onCourseDetailsThumbnailChange = ({ target: { files } }, setFieldValue) => {
    setFieldValue('courseDetailsThumbnail', files[0])
  }

  const onVideoChange = ({ target: { files } }, setFieldValue) => {
    setFieldValue('video', files[0])
  }

  return (
    <>
      <Box sx={{ m: 1 }}>
        <Container>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              values,
              handleBlur,
              handleChange,
              setFieldValue,
              setFieldError,
              handleSubmit,
              isSubmitting,
            }) => (
              <Form>
                <Grid container spacing={2}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <TextField
                        error={Boolean(touched.name && errors.name)}
                        fullWidth
                        helperText={touched.name && errors.name}
                        label="Name"
                        name="name"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.name}
                        required
                        size="small"
                      />

                      <TextField
                        error={Boolean(touched.description && errors.description)}
                        fullWidth
                        helperText={touched.description && errors.description}
                        label="Description"
                        name="description"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.description}
                        required
                        size="small"
                        multiline
                        rows={4}
                      />

                      <FormControl error={Boolean(touched.course && errors.course)} fullWidth>

                        <DropdownMenu
                          value={values?.course}
                          label="Language"
                          name="course"
                          onChange={handleChange}
                          isRequired={true}
                          data={dropdowns?.courses}
                        />
                        <FormHelperText>{touched?.course && errors?.course}</FormHelperText>
                      </FormControl>

                      <FormControl fullWidth>
                        <DropdownMenu
                          value={values?.difficultyLevel}
                          label="Difficulty Level"
                          name="difficultyLevel"
                          onChange={handleChange}
                          isRequired={true}
                          data={courseDifficultyLevel}
                        />
                      </FormControl>

                      <Box width="full">
                        <VideoThumbnailField
                          imageUrl={values?.videoThumbnail}
                          name="videoThumbnail"
                          onChange={e => onThumbnailChange(e, setFieldValue)}
                        />
                        <ImageRatioSize ratio="46∶51" minWidth="230" size="500kb" />
                      </Box>

                    </Stack>
                  </Grid>

                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={2}>
                      <Box width="full">
                        <VideoThumbnailField
                          imageUrl={values?.courseDetailsThumbnail}
                          name="courseDetailsThumbnail"
                          id="course-details-thumbnail"
                          label="UPLOAD COURSE DETAILS THUMBNAIL"
                          onChange={e => onCourseDetailsThumbnailChange(e, setFieldValue)}
                        />
                        <ImageRatioSize ratio="194∶109" minWidth="582px" size="500kb" />
                      </Box>

                      <VideoUploaderField
                        onChange={e => onVideoChange(e, setFieldValue)}
                        fileKeys={{}}
                        src={values?.exerciseVideo}
                      />

                      <FormControl fullWidth>
                        <DropdownMenu
                          value={values.isActive}
                          label="Status"
                          name="isActive"
                          isRequired={true}
                          onChange={handleChange}
                          data={StatusList}
                        />
                      </FormControl>

                      <FormControl fullWidth>
                        <DropdownMenu
                          value={values.unPublished}
                          label="Visibility"
                          name="unPublished"
                          isRequired={true}
                          onChange={handleChange}
                          data={VisibilityList}
                        />
                      </FormControl>

                      <TextField
                        fullWidth
                        label="Sort Order"
                        inputProps={{ min: 0 }}
                        name="sortOrder"
                        type="number"
                        defaultValue={0}
                        onChange={handleChange}
                        value={values?.sortOrder}
                        variant="outlined"
                        size="small"
                      />

                      <Box sx={{ paddingLeft: 2 }}>
                        <Typography sx={{ font: 'inherit', mb: 1 }}>Duration of Course</Typography>
                        <Stack direction="row" spacing={2}>
                          <FormControlLabel
                            label={<Typography sx={{ p: '10px' }}>HH</Typography>}
                            control={
                              <DropdownMenu
                                value={values.hrs}
                                name="hrs"
                                onChange={handleChange}
                                isRequired={true}
                                data={hrs}
                              />
                            }
                          />
                          <FormControlLabel
                            label={<Typography sx={{ p: '10px' }}>MM</Typography>}
                            control={
                              <DropdownMenu
                                value={values.minutes}
                                name="minutes"
                                onChange={handleChange}
                                isRequired={true}
                                data={minutes}
                              />
                            }
                          />
                        </Stack>
                      </Box>
                    </Stack>
                  </Grid>

                  {/* Buttons Row */}
                  <Grid item xs={12} md={6}>
                    <Button
                      color="primary"
                      onClick={toggleDrawer}
                      fullWidth
                      size="large"
                      type="button"
                      variant="outlined"
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LoadingButton
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      name="Save"
                    />
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Container>
      </Box>
    </>
  )
}

export default AddEditCourse
